import { useMemo } from "react";
import { useLocale } from "react-aria";
import clsx from "clsx";

import { ClockIcon } from "~/components/Icons/ClockIcon";
import { Text } from "~/components/Text";
import { useStateManager } from "~/modules/StateManager.hooks";
import { useEditTimetableLoaderData } from "~/routes/$enterprise+/plugin+/$sessionId.appointment.edit.timetable";
import { useInviteTimetableLoaderData } from "~/routes/$enterprise+/plugin+/$sessionId.appointment.invite.timetable";
import { useScheduleTimetableLoaderData } from "~/routes/$enterprise+/plugin+/$sessionId.schedule.timetable";
import { DurationFormat, millisecondsToTimeUnits } from "~/utils/date";

interface SubjectSummaryProps {
  title: string;
  start?: string;
  end?: string;
  externalId?: string | null;
  className?: string;
}

export function SubjectSummary({ className, externalId, title, ...props }: SubjectSummaryProps) {
  const timetableData = useTimetableLoaderData();

  const { locale } = useLocale();

  const timetable = useStateManager().state.timetable;

  const start = props.start ?? timetable?.start;
  const end = props.end ?? timetable?.end;

  const appointmentDuration = useMemo(() => {
    const slot = timetableData?.timetableData
      ? Object.values(timetableData.timetableData)
          .flat()
          .find((slot) => slot.start)
      : undefined;

    const startISO = start ?? slot?.start;
    const endISO = end ?? slot?.end;

    if (!startISO || !endISO) return;

    const startDate = new Date(startISO);
    const endDate = new Date(endISO);

    const timeUnits = millisecondsToTimeUnits(endDate.getTime() - startDate.getTime());
    const formatter = new DurationFormat(locale);

    return formatter.format(timeUnits);
  }, [start, end, locale, timetableData?.timetableData]);

  return (
    <div
      className={clsx("grid gap-3xs", className)}
      data-component="SubjectSummary"
      data-external-id={externalId}
    >
      <span className="text-sm font-semibold text-regular" data-component="SubjectSummaryLabel">
        <Text i18nKey="steps.Subject.summaryTitle" />
      </span>
      <span className="text-xl font-bold text-secondary" data-component="SubjectSummaryTitle">
        {title}
      </span>
      {appointmentDuration && <Duration appointmentDuration={appointmentDuration} />}
    </div>
  );
}

function Duration({ appointmentDuration }: { appointmentDuration: string }) {
  return (
    <div
      className={clsx("flex items-center gap-xs", "text-sm font-normal", "text-gray-500")}
      data-component="SubjectSummaryDuration"
    >
      <div className="h-[20px] w-[20px] text-brand">
        <ClockIcon />
      </div>
      {appointmentDuration}
    </div>
  );
}

function useTimetableLoaderData() {
  const scheduleTimetableData = useScheduleTimetableLoaderData();
  const editTmetableData = useEditTimetableLoaderData();
  const inviteTimetableData = useInviteTimetableLoaderData();

  return scheduleTimetableData ?? editTmetableData ?? inviteTimetableData;
}
